import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, ElementRef, forwardRef, inject, input, output, signal, viewChild } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { BlobUploadService, ImagePreview } from '../../services/blob-upload.service';
import { SecurePipe } from '../../pipes/secure.pipe';

export interface ImageState {
  name: string;
  url: string;
  reference: string;
  key?: string;
  kind?: 'new';
}

@Component({
  selector: 'app-image-selector',
  standalone: true,
  imports: [CommonModule, MatProgressSpinnerModule, SecurePipe],
  templateUrl: './image-selector.component.html',
  styleUrl: './image-selector.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ImageSelectorComponent),
      multi: true,
    },
  ],
})
export class ImageSelectorComponent implements ControlValueAccessor {
  private readonly blobUploadService = inject(BlobUploadService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly activeObjectUrls = new Set<string>();

  // Inputs
  readonly mode = input<'single' | 'multiple'>('single');
  readonly buttonLabel = input<string>('');
  readonly sessionKey = input.required<string>();
  readonly maxImages = input(10);

  // Computed button label
  readonly effectiveButtonLabel = computed(() => {
    const label = this.buttonLabel();
    if (label) return label;
    return this.mode() === 'single' ? 'Select Preview Image' : 'Add Gallery Images';
  });

  // Outputs
  readonly referenceChange = output<string | null>(); // For single mode
  readonly uploadingChange = output<boolean>(); // For single mode
  readonly uploadingCountChange = output<number>(); // For multiple mode

  // State
  readonly previews = signal<ImagePreview[]>([]);
  readonly uploadingCount = signal(0);
  readonly error = signal<string | null>(null);
  readonly disabled = signal(false);

  readonly isAtLimit = computed(() => this.previews().length >= this.maxImages());
  readonly isSingleMode = computed(() => this.mode() === 'single');
  readonly isMultipleMode = computed(() => this.mode() === 'multiple');
  
  // Single mode specific
  readonly singlePreview = computed(() => this.previews()[0] ?? null);
  readonly isUploading = computed(() => this.uploadingCount() > 0);

  // ViewChild
  readonly fileInput = viewChild<ElementRef<HTMLInputElement>>('fileInput');

  private files: File[] = [];
  private uploadSeq = 0;
  private onChange: (value: File | File[] | null) => void = () => {};
  private onTouched: () => void = () => {};

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.revokeAllObjectUrls();
    });
  }

  // ControlValueAccessor
  writeValue(value: File | File[] | null): void {
    this.revokeAllObjectUrls();
    this.previews.set([]);
    this.files = [];

    if (!value) return;

    if (this.isSingleMode()) {
      const file = value as File;
      this.files = [file];
      const url = this.createObjectUrl(file);
      const reference = this.blobUploadService.buildStampedReference(file.name);
      this.previews.set([{
        key: this.blobUploadService.fileKey(file),
        name: file.name,
        reference,
        url,
        kind: 'new',
      }]);
      this.referenceChange.emit(reference);
    } else {
      const fileArray = Array.isArray(value) ? value : [value];
      this.files = [...fileArray];
      const newPreviews = fileArray.map(f => ({
        key: this.blobUploadService.fileKey(f),
        name: f.name,
        reference: this.blobUploadService.buildStampedReference(f.name),
        url: this.createObjectUrl(f),
        kind: 'new' as const,
      }));
      this.previews.set(newPreviews);
    }
  }

  registerOnChange(fn: (value: File | File[] | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  // Public methods for parent component
  getReferencesList(): string[] {
    return this.previews()
      .map(p => p.reference)
      .filter(Boolean);
  }

  getReferences(): Map<string, string> {
    const map = new Map<string, string>();
    for (const p of this.previews()) {
      map.set(p.key, p.reference);
    }
    return map;
  }

  // Actions
  triggerFileInput(): void {
    this.fileInput()?.nativeElement.click();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    
    if (this.isSingleMode()) {
      this.handleSingleFileSelection(input);
    } else {
      this.handleMultipleFileSelection(input);
    }
  }

  private handleSingleFileSelection(input: HTMLInputElement): void {
    const file = input.files?.[0] ?? null;
    
    this.error.set(null);

    if (file) {
      const validation = this.blobUploadService.validateFile(file);
      if (!validation.valid) {
        this.error.set(validation.error);
        input.value = '';
        return;
      }
    }

    // Revoke old URL
    this.revokeObjectUrl(this.singlePreview()?.url);

    if (file) {
      const url = this.createObjectUrl(file);
      const reference = this.blobUploadService.buildStampedReference(file.name);
      this.previews.set([{
        key: this.blobUploadService.fileKey(file),
        name: file.name,
        reference,
        url,
        kind: 'new',
      }]);
      this.files = [file];
      this.referenceChange.emit(reference);
      this.onChange(file);
      this.onTouched();

      void this.uploadSingleImage(file, reference);
    } else {
      this.previews.set([]);
      this.files = [];
      this.referenceChange.emit(null);
      this.onChange(null);
    }

    input.value = '';
  }

  private handleMultipleFileSelection(input: HTMLInputElement): void {
    const newFiles = input.files ? Array.from(input.files) : [];

    this.error.set(null);

    if (this.files.length >= this.maxImages()) {
      this.error.set(`Maximum of ${this.maxImages()} images allowed.`);
      input.value = '';
      return;
    }

    // Validate each file
    for (const file of newFiles) {
      const validation = this.blobUploadService.validateFile(file);
      if (!validation.valid) {
        this.error.set(validation.error);
        input.value = '';
        return;
      }
    }

    // Deduplicate files
    const uniqueNewFiles = this.blobUploadService.deduplicateFiles(this.files, newFiles);

    if (uniqueNewFiles.length === 0) {
      this.error.set('Selected files are already in the gallery.');
      input.value = '';
      return;
    }

    // Check if adding would exceed limit
    const totalAfterAdd = this.files.length + uniqueNewFiles.length;
    if (totalAfterAdd > this.maxImages()) {
      this.error.set(
        `Can only add ${this.maxImages() - this.files.length} more image(s). Maximum is ${this.maxImages()}.`
      );
      input.value = '';
      return;
    }

    // Add files
    this.files = [...this.files, ...uniqueNewFiles];

    // Create previews
    const newPreviews = uniqueNewFiles.map(f => ({
      key: this.blobUploadService.fileKey(f),
      name: f.name,
      reference: this.blobUploadService.buildStampedReference(f.name),
      url: this.createObjectUrl(f),
      kind: 'new' as const,
    }));
    this.previews.update(p => [...p, ...newPreviews]);

    this.onChange(this.files);
    this.onTouched();

    void this.uploadMultipleImages(uniqueNewFiles);

    input.value = '';
  }

  clearImage(): void {
    this.uploadSeq += 1;
    this.uploadingCount.set(0);
    this.uploadingChange.emit(false);

    this.revokeObjectUrl(this.singlePreview()?.url);
    this.previews.set([]);
    this.files = [];
    this.referenceChange.emit(null);
    this.onChange(null);
    this.onTouched();
  }

  removeImage(index: number): void {
    const preview = this.previews()[index];
    if (!preview) return;

    // Remove preview
    this.previews.update(p => p.filter((_, i) => i !== index));

    // Remove file
    this.files = this.files.filter(f => this.blobUploadService.fileKey(f) !== preview.key);
    this.revokeObjectUrl(preview.url);

    this.onChange(this.files.length > 0 ? this.files : []);
    this.onTouched();
  }

  clearAll(): void {
    for (const p of this.previews()) {
      this.revokeObjectUrl(p.url);
    }

    this.files = [];
    this.previews.set([]);
    this.onChange([]);
    this.onTouched();
  }

  // Upload methods
  private async uploadSingleImage(file: File, reference: string): Promise<void> {
    const seq = ++this.uploadSeq;
    this.uploadingCount.set(1);
    this.uploadingChange.emit(true);

    try {
      await this.blobUploadService.uploadPreview(file, this.sessionKey(), reference);
    } catch (err) {
      console.error('Error uploading preview image:', err);

      if (seq !== this.uploadSeq) return;

      this.error.set('Unable to upload image. Please try again.');
      this.clearImage();
    } finally {
      if (seq === this.uploadSeq) {
        this.uploadingCount.set(0);
        this.uploadingChange.emit(false);
      }
    }
  }

  private async uploadMultipleImages(files: File[]): Promise<void> {
    if (!files.length) return;

    this.uploadingCount.update(c => c + files.length);
    this.uploadingCountChange.emit(this.uploadingCount());

    try {
      const referenceByKey = this.getReferences();
      const { failedKeys, failedNames } = await this.blobUploadService.uploadGalleryImages(
        files,
        this.sessionKey(),
        referenceByKey
      );

      if (failedKeys.size > 0) {
        // Remove failed files
        this.files = this.files.filter(f => !failedKeys.has(this.blobUploadService.fileKey(f)));

        // Remove failed previews and revoke URLs
        const currentPreviews = this.previews();
        for (const p of currentPreviews) {
          if (failedKeys.has(p.key)) {
            this.revokeObjectUrl(p.url);
          }
        }
        this.previews.update(p => p.filter(prev => !failedKeys.has(prev.key)));

        this.error.set(`Unable to upload ${failedNames.length} image(s): ${failedNames.join(', ')}.`);
        this.onChange(this.files);
      }
    } catch (err) {
      console.error('Error uploading gallery images:', err);
      this.error.set('Unable to upload images. Please try again.');
    } finally {
      this.uploadingCount.update(c => Math.max(0, c - files.length));
      this.uploadingCountChange.emit(this.uploadingCount());
    }
  }

  // ObjectURL management
  private createObjectUrl(file: File): string {
    const url = URL.createObjectURL(file);
    this.activeObjectUrls.add(url);
    return url;
  }

  private revokeObjectUrl(url: string | null | undefined): void {
    if (!url) return;
    if (!this.activeObjectUrls.has(url)) return;
    this.activeObjectUrls.delete(url);
    URL.revokeObjectURL(url);
  }

  private revokeAllObjectUrls(): void {
    for (const url of this.activeObjectUrls) {
      URL.revokeObjectURL(url);
    }
    this.activeObjectUrls.clear();
  }
}

